/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module PaymentTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

export interface Payment {
    [k: string]: any;
}
/**
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "StoreInfoResponse".
 */
export interface StoreInfoResponse {
    /**
     * User identifier provided by the store.
     */
    storeUserId?: string;
    /**
     * Store marketplace as an ISO 3166-1 alpha-2 code, when provided by the store.
     */
    marketplace?: string;
    /**
     * User country as an ISO 3166-1 alpha-2 code, when provided by the store.
     */
    countryCode?: string;
}
/**
 * Single pricing plan of a promotion.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PromotionPlan".
 */
export interface PromotionPlan {
    /**
     * Localized promotional price including currency symbol (e.g. '$4.00').
     */
    displayPrice: string;
    period: PeriodUnit;
    /**
     * Number of billing cycles the promotional price applies for.
     */
    cycles: number;
}
/**
 * Promotional pricing offer configured for the product.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "Promotion".
 */
export interface Promotion {
    /**
     * Promotion type as reported by the store. Currently only 'Introductory'; kept as a free-form string for forward compatibility.
     */
    promotionType: string;
    promotionPlans: PromotionPlan[];
}
/**
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "GetProductsDataParameters".
 */
export interface GetProductsDataParameters {
    productIds: string[];
}
/**
 * IAP product from GetProductsData. subscriptionPeriod and promotions are set only when configured for the product (subscriptions).
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "ProductInfo".
 */
export interface ProductInfo {
    productId: string;
    displayName: string;
    description: string;
    /**
     * Localized regular price (e.g. '$9.99').
     */
    displayPrice: string;
    subscriptionPeriod?: PeriodUnit;
    /**
     * Promotional (e.g. introductory) pricing offers.
     */
    promotions?: Promotion[];
}
/**
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "ProductsDataResponse".
 */
export interface ProductsDataResponse {
    products: ProductInfo[];
    /**
     * User identifier provided by the store.
     */
    storeUserId?: string;
}
/**
 * Parameters for PurchaseSubscription. prorationMode supersedes purchaseType; both are accepted while callers migrate, and prorationMode wins if both are set.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PurchaseSubscriptionParameters".
 */
export interface PurchaseSubscriptionParameters {
    productId: string;
    purchaseType?: PurchaseType;
    prorationMode?: ProrationMode;
}
/**
 * Return shape of PurchaseSubscription: the completed purchase plus the store user that owns it.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PurchaseProductResponse".
 */
export interface PurchaseProductResponse {
    purchase: PurchaseItem;
    /**
     * Store user ID that owns the purchase; needed for backend receipt verification.
     */
    storeUserId?: string;
}
/**
 * A purchase owned by the store user. Covers subscriptions and consumables (includes any unfulfilled receipt).
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PurchaseItem".
 */
export interface PurchaseItem {
    productId: string;
    /**
     * Subscriptions: false once expired/cancelled. Consumables: always true (valid until fulfilled).
     */
    isActive: boolean;
    transactionOrReceiptId: string;
}
/**
 * Shared return shape of the purchase-listing commands: GetPurchaseUpdates returns those updated since the last call, RestorePurchases the full history.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PurchaseUpdatesResponse".
 */
export interface PurchaseUpdatesResponse {
    purchases: PurchaseItem[];
    /**
     * Store user ID that owns the returned purchases; needed for backend receipt verification.
     */
    storeUserId?: string;
}
/**
 * Identifies the transaction the UI is acknowledging after backend receipt verification.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "AcknowledgeFulfillmentParameters".
 */
export interface AcknowledgeFulfillmentParameters {
    /**
     * The transactionOrReceiptId returned by a previous PurchaseSubscription / GetPurchaseUpdates call.
     */
    transactionOrReceiptId: string;
    result?: FulfillmentResult;
}

/**
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PeriodUnit".
 */
export enum PeriodUnit {
    Week = 'Week',
    Month = 'Month',
    Year = 'Year',
    Biweek = 'Biweek',
    Bimonth = 'Bimonth',
    Quarter = 'Quarter',
    Semiannual = 'Semiannual'
}
/**
 * DEPRECATED, use prorationMode. Changes an active subscription instead of starting one. 'Upgrade' applies right away, 'Downgrade' at the end of the billing cycle.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "PurchaseType".
 */
export enum PurchaseType {
    Upgrade = 'Upgrade',
    Downgrade = 'Downgrade'
}
/**
 * When a subscription change applies. 'Immediate' switches plan and charges now, 'Deferred' at the end of the billing cycle. Supersedes purchaseType.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "ProrationMode".
 */
export enum ProrationMode {
    Immediate = 'Immediate',
    Deferred = 'Deferred'
}
/**
 * 'Unavailable' triggers a refund and stops re-delivery. Defaults to Fulfilled.
 *
 * This interface was referenced by `Payment`'s JSON-Schema
 * via the `definition` "FulfillmentResult".
 */
export enum FulfillmentResult {
    Fulfilled = 'Fulfilled',
    Unavailable = 'Unavailable'
}


}
